// Topology & Strategy — shape-metric tiles + the ranked white-space list +
// an evidence-grounded action sequence, per screen7-topology.png (Story
// #5115 Stream 6f, Task #5131). Self-contained like PortfolioAtlas.tsx/
// SynergyMatrix.tsx: resolves its own aggregator context and fetches its
// own data.
//
// HONEST GAPS vs. the mockup (documented, never fabricated): the mockup's
// "CPC sections" / "Themes (cross-project)" / "Overlap P1·P3" / "Encroachment
// pressure (90 days)" panel all need endpoints this story's dependencies
// (#5126/#5129/#5130) don't provide — no CPC-subclass rollup, no cross-
// project theme clustering route, no pairwise document-overlap %, and no
// filing-drift-over-time detector exist yet. Rather than inventing those
// numbers, the tiles below show only what #5126/#5129/#5130/#5119 actually
// return (holes+bridges, frontier directions, member projects, cross-project
// synergy cells, and the topology guardrail's candidate/discarded counts),
// and the encroachment panel states the gap plainly instead of rendering a
// fake "0 filings" reading.
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import {
  getPortfolioTopology,
  getPortfolioScore,
  getPortfolioSynergy,
  getAggregatorPortfolioProjects,
} from '../../services/api'
import {
  buildRankedWhitespaceItems,
  buildActionSequence,
  projectTagColor,
  PORTFOLIO_INSUFFICIENT_BASIS_REASON,
  type RankedWhitespaceItem,
} from '../../lib/whitespace'
import { useAggregator } from '../../contexts/AggregatorContext'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import ConfidenceGate from '../shared/ConfidenceGate'

const TAG_STYLE: Record<string, { background: string; color: string; border?: string }> = {
  hole: { background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' },
  bridge: { background: '#EDE9FE', color: '#6D28D9' },
  frontier_edge: { background: '#E0F2FE', color: '#0369A1' },
  synergy: { background: '#EDE9FE', color: '#6D28D9' },
}

const SCORE_BADGE_STYLE = (score: number): { background: string; color: string } => {
  if (score >= 85) return { background: '#FBBF24', color: '#111827' }
  if (score >= 70) return { background: '#FBBF24', color: '#111827' }
  return { background: '#FDE68A', color: '#111827' }
}

export default function TopologyStrategy() {
  const { activeAggregatorId } = useAggregator()

  const projectsQuery = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
  })
  const topologyQuery = useQuery({
    queryKey: ['portfolio-topology', activeAggregatorId],
    queryFn: () => getPortfolioTopology(activeAggregatorId),
  })
  const scoreQuery = useQuery({
    queryKey: ['portfolio-score', activeAggregatorId],
    queryFn: () => getPortfolioScore(activeAggregatorId),
  })
  const synergyQuery = useQuery({
    queryKey: ['portfolio-synergy', activeAggregatorId],
    queryFn: () => getPortfolioSynergy(activeAggregatorId),
  })

  const names = useMemo(
    () => new Map((projectsQuery.data?.projects ?? []).map((p) => [p.project_id, p.name])),
    [projectsQuery.data],
  )

  const regions = scoreQuery.data?.regions ?? []
  const rankedItems = useMemo(
    () =>
      buildRankedWhitespaceItems(
        regions,
        synergyQuery.data?.cells ?? [],
        synergyQuery.data?.pair_scores ?? [],
        names,
      ),
    [regions, synergyQuery.data, names],
  )
  const actionSequence = useMemo(() => buildActionSequence(rankedItems), [rankedItems])

  const isLoading = topologyQuery.isLoading || scoreQuery.isLoading || synergyQuery.isLoading
  const isError = topologyQuery.isError || scoreQuery.isError || synergyQuery.isError

  if (isLoading) {
    return (
      <div data-testid="topology-strategy-loading">
        <LoadingSkeleton type="card" rows={3} />
      </div>
    )
  }

  if (isError) {
    const err = (topologyQuery.error ?? scoreQuery.error ?? synergyQuery.error) as Error | null
    return (
      <div className="p-4 text-sm text-red-600" role="alert" data-testid="topology-strategy-error">
        Couldn't load topology &amp; strategy: {err?.message ?? 'unknown error'}
      </div>
    )
  }

  const memberProjectIds = scoreQuery.data?.member_project_ids ?? []
  const holeCount = topologyQuery.data?.holes.length ?? 0
  const bridgeCount = topologyQuery.data?.bridges.length ?? 0
  const frontierCount = topologyQuery.data?.frontier_edges.length ?? 0

  return (
    <div data-testid="topology-strategy">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Tile value={`${memberProjectIds.length}`} label="Member projects" detail={memberProjectIds.map((id) => names.get(id) ?? id).join(', ')} />
        <Tile
          value={`${holeCount} + ${bridgeCount}`}
          label="Holes + bridges"
          tone="amber"
          detail={`${holeCount} enclosed gap${holeCount === 1 ? '' : 's'}, ${bridgeCount} empty corridor${bridgeCount === 1 ? '' : 's'} between own clusters`}
        />
        <Tile value={`${frontierCount}`} label="Open ground" detail="Open directions on the footprint's outer edge" />
        <Tile
          value={`${synergyQuery.data?.cross_project_cell_count ?? 0}`}
          label="Cross-project synergy cells"
          detail="Stitched matrix cells only this portfolio can see"
        />
        <Tile
          value={`${topologyQuery.data?.discarded_candidate_count ?? 0} / ${topologyQuery.data?.candidate_count ?? 0}`}
          label="Left off the map"
          detail="Candidates the full-space validation gate rejected"
        />
      </div>

      <div className="flex gap-4 flex-col lg:flex-row">
        <div className="light-card p-4 flex-[1.35]">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h2 className="text-sm font-semibold text-iflow-900">Portfolio white space, ranked</h2>
            <span className="text-[11px] text-gray-500">score = WOS-P (sparsity + adjacency + momentum + enablement + enclosure + synergy)</span>
            <div className="flex-1" />
          </div>
          {/* design §5.7: "a legend finally explains the letters" — each id's
              prefix (whitespace/portfolio_topology.py::_STABLE_ID_PREFIX for
              H/B/F; lib/whitespace.ts's KIND_PREFIX for S) is a durable
              identity that survives a recompute, but the LETTER itself is
              never explained where the id is shown compactly elsewhere
              (e.g. the Atlas's own badges) — spelled out here once. */}
          {rankedItems.length > 0 && (
            <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[10px] text-gray-500 mb-2" data-testid="stable-id-legend">
              <span className="font-semibold text-gray-400 uppercase tracking-wide">Id letters</span>
              <span><b className="text-amber-700">H</b> Hole</span>
              <span><b className="text-violet-700">B</b> Bridge</span>
              <span><b className="text-sky-700">F</b> Open ground</span>
              <span><b className="text-violet-700">S</b> Synergy</span>
            </div>
          )}

          {rankedItems.length === 0 ? (
            /* design §5.5: "zero findings with eleven discarded candidates
               is a diagnostic, not a blank screen" — the tiles above always
               render the real counts; this panel states the same fact in
               words rather than showing nothing at all. */
            <p className="text-[11.5px] text-gray-500 mt-2 leading-relaxed" data-testid="topology-no-findings">
              No hole, bridge, open ground, or cross-project synergy cell has survived full-space validation yet —
              see "Left off the map" above for how many candidates were checked and rejected.
            </p>
          ) : (
            <div className="flex flex-col gap-2 mt-2">
              {rankedItems.map((item, i) => (
                <RankedRow key={item.id} item={item} names={names} memberProjectIds={memberProjectIds} highlight={i === 0} />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-3">
          <div className="light-card p-4">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Encroachment pressure (90 days)
            </div>
            <p className="text-[11.5px] text-gray-600 mt-2 leading-relaxed">
              Not available yet — tracking external filings drifting toward the portfolio's footprint over time needs
              a drift-detection endpoint that hasn't shipped yet. This panel will read from that endpoint once it
              exists, rather than showing an invented reading now.
            </p>
          </div>

          <div className="light-card p-4 flex-1">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Where to act next — generated from the evidence above
            </div>
            <div className="flex flex-col gap-2 mt-2.5">
              {actionSequence.length > 0 ? (
                actionSequence.map((line, i) => (
                  <p key={i} className="text-[11.8px] text-gray-700 leading-relaxed">
                    {line}
                  </p>
                ))
              ) : (
                <p className="text-[11.8px] text-gray-500 leading-relaxed">
                  Nothing to act on yet — no finding above has survived full-space validation.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Tile({ value, label, detail, tone }: { value: string; label: string; detail?: string; tone?: 'amber' }) {
  return (
    <div className="light-card p-3">
      <div className={`text-[19px] font-extrabold ${tone === 'amber' ? 'text-amber-700' : 'text-iflow-900'}`}>{value}</div>
      <div className="text-[9.5px] font-bold text-gray-500 uppercase tracking-wide">{label}</div>
      {detail && <div className="text-[10.5px] text-gray-600 mt-0.5 truncate" title={detail}>{detail}</div>}
    </div>
  )
}

function RankedRow({
  item,
  names,
  memberProjectIds,
  highlight,
}: {
  item: RankedWhitespaceItem
  names: Map<string, string>
  memberProjectIds: string[]
  highlight: boolean
}) {
  const badge = SCORE_BADGE_STYLE(item.score)
  const tag = TAG_STYLE[item.kind]
  return (
    <div
      data-testid={`ranked-item-${item.id}`}
      className={`flex items-start gap-2.5 border rounded-lg bg-white px-3 py-2.5 ${
        highlight ? 'border-amber-400 shadow-[0_0_0_2px_#FDE68A]' : 'border-sky-100'
      }`}
    >
      <span
        className="flex items-center justify-center w-10 h-10 rounded-full font-extrabold text-[13px] flex-shrink-0"
        style={{ background: badge.background, color: badge.color }}
      >
        {item.score}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-bold text-iflow-900 flex items-center gap-1.5 flex-wrap">
          {item.id} — {item.tagLabel === 'ENCLOSED HOLE' ? 'Autonomous white-space region' : item.tagLabel === 'SYNERGY' ? 'Top cross-project synergy' : item.tagLabel === 'BRIDGE' ? 'Internal corridor' : 'Open ground'}
          <span
            className="inline-flex text-[9.5px] font-extrabold rounded-full px-2 py-0.5"
            style={{ background: tag?.background, color: tag?.color, border: tag?.border }}
          >
            {item.tagLabel}
          </span>
        </div>
        <div className="text-[11px] text-gray-600 leading-relaxed mt-0.5">
          {item.detail}{' '}
          {item.memberProjectIds.length > 0 && (
            <span className="inline-flex items-center gap-1 ml-1">
              {item.memberProjectIds.map((id) => (
                <span
                  key={id}
                  className="w-2 h-2 rounded-sm inline-block"
                  style={{ background: projectTagColor(id, memberProjectIds) }}
                  title={names.get(id) ?? id}
                />
              ))}
            </span>
          )}
        </div>
        {item.insufficientBasis && (
          <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50/60">
            <ConfidenceGate
              icon={<AlertTriangle />}
              title="Sub-scores are provisional"
              description={PORTFOLIO_INSUFFICIENT_BASIS_REASON}
              compact
            />
          </div>
        )}
      </div>
    </div>
  )
}
